import logo from "@static/img/logo-pink-bg.svg";
import { compact } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

import AskAI from "../ask-ai/ask-ai";
import ChatHistoryPopover from "./components/chat-history-popover";
import Insights from "./components/insights";
import HomePeopleTable from "./components/people";

const Home = () => {
  const link = useLink();
  const params = useParams<{ chatSessionId: string }>();
  const askAIPathName = "/ask-ai";
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    params.chatSessionId ? parseInt(params.chatSessionId) : null,
  );
  const [startedChatSession, setStartedChatSession] = useState(
    !!askAIChatSessionId || location.pathname.includes(askAIPathName),
  );
  useDocumentTitle(startedChatSession ? "Ask AI" : "Home");
  const hasDirectReports = currentUser.directReports.edges.length > 0;

  const isAskAIEnabled = !!currentOrganization.featureFlags.askAi;
  const initialPrompt =
    new URLSearchParams(location.search).get("p") || undefined;

  // when the session id changes, update the url
  useEffect(() => {
    if (
      askAIChatSessionId &&
      (!params.chatSessionId ||
        params.chatSessionId !== String(askAIChatSessionId))
    ) {
      link.replace(`${askAIPathName}/${askAIChatSessionId}`);
    }
  }, [askAIChatSessionId]);

  // when the url changes, update the session id
  useEffect(() => {
    setStartedChatSession(location.pathname.includes(askAIPathName));

    if (askAIChatSessionId && !params.chatSessionId) {
      setAskAIChatSessionId(null);
    } else if (
      params.chatSessionId &&
      (!askAIChatSessionId ||
        params.chatSessionId !== String(askAIChatSessionId))
    ) {
      setAskAIChatSessionId(parseInt(params.chatSessionId));
    }
  }, [params.chatSessionId, location.pathname]);

  const fullAskAIView =
    isAskAIEnabled && (startedChatSession || !!askAIChatSessionId);

  const userName = currentUser.firstName || null;
  return (
    <Layout>
      {isAskAIEnabled && (
        <div className="absolute z-1 top-2 right-4 flex items-center gap-4">
          <Button to="/ask-ai" icon={getTopicflowIcon("pencilSquare")} />
          <ChatHistoryPopover />
        </div>
      )}
      <Layout.Container noHeader={fullAskAIView}>
        <Layout.Main
          subClassName={classNames(
            "flex flex-col h-full relative",
            fullAskAIView ? "pb-0" : "",
          )}
          className={classNames(fullAskAIView ? "pt-0 md:pt-0" : "")}
          width={fullAskAIView ? "full" : "default"}
        >
          {!fullAskAIView && (
            <div className="mt-0  tf-header-2xl font-semibold tracking-tight flex items-center gap-3 flex-col justify-center">
              <img
                src={getAssetUrl(logo)}
                alt="Topicflow"
                className="block h-10 w-10"
              />
              <div>Welcome back{userName ? `, ${userName}` : ""}!</div>
            </div>
          )}

          {isAskAIEnabled && (
            <AskAI
              isInSidebar={false}
              chatSessionId={askAIChatSessionId}
              onChangeChatSessionId={setAskAIChatSessionId}
              onStartChatSession={() => setStartedChatSession(true)}
              className={classNames(!fullAskAIView && "mt-6")}
              isFullScreen={fullAskAIView}
              totalYPaddingAroundContainer={0}
              initialPrompt={initialPrompt}
              formButtons={compact([
                {
                  text: hasDirectReports ? "Team Updates" : "My Updates",
                  prompt: hasDirectReports
                    ? "Give me an overview of my direct reports for the past 7 days."
                    : "Give me an overview of my work for the past 7 days.",
                  icon: hasDirectReports ? "users" : "user",
                },
              ])}
            />
          )}

          {!fullAskAIView && (
            <div className="py-10 flex flex-col gap-10">
              <Insights selectedUser={currentUser} />
              <HomePeopleTable />
            </div>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default Home;
